import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import {
  IModalCTACallbacks,
  IModalCTAContext,
  IModalCTAProvider,
  IValuesForModal,
} from './interfaces';
import ConfirmModal from '@components/ConfirmModal';
import ConfirmLogoutModal from '@components/ConfirmLogoutModal';

const ModalCTAContext = createContext<IModalCTAContext | null>(null);

export const useModalCTAContext = () =>
  useContext(ModalCTAContext) as IModalCTAContext;

const ModalCTAProvider = ({ children }: IModalCTAProvider) => {
  const [valuesForModal, setValuesForModal] = useState<IValuesForModal | null>(
    null,
  );
  const callbacksRef = useRef<IModalCTACallbacks>({});

  const onCloseModal = () => {
    callbacksRef.current.onClose?.();
    setValuesForModal(null);
    callbacksRef.current = {};
  };

  const onCancelModal = () => {
    callbacksRef.current.onCancel?.();
    setValuesForModal(null);
    callbacksRef.current = {};
  };

  const onConfirmModal = () => {
    callbacksRef.current.onConfirm?.();
    setValuesForModal(null);
    callbacksRef.current = {};
  };

  const onCancelAltAction = () => {
    callbacksRef.current.onCancelAltAction?.();
    setValuesForModal(null);
    callbacksRef.current = {};
  };

  const handleValuesForModal = useCallback(
    (payload: IValuesForModal | null) => {
      if (payload === null) {
        setValuesForModal(null);
        callbacksRef.current = {};
        return;
      }

      const { onCancel, onClose, onConfirm, onCancelAltAction, ...rest } =
        payload;

      callbacksRef.current = {
        onCancel: onCancel ?? undefined,
        onClose: onClose ?? undefined,
        onConfirm: onConfirm ?? undefined,
        onCancelAltAction: onCancelAltAction ?? undefined,
      };

      setValuesForModal(rest);
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      setValuesForModal: handleValuesForModal,
    }),
    [setValuesForModal, handleValuesForModal],
  );

  return (
    <ModalCTAContext.Provider value={contextValue}>
      {children}
      {valuesForModal?.isConfirmLogoutModal && (
        <ConfirmLogoutModal
          open={valuesForModal?.open || false}
          onClose={onCloseModal}
          onConfirm={onConfirmModal}
          altStyling
        />
      )}
      {!valuesForModal?.isConfirmLogoutModal && (
        <ConfirmModal
          icon={valuesForModal?.icon}
          title={valuesForModal?.title}
          message={valuesForModal?.message}
          open={valuesForModal?.open || false}
          onCancel={callbacksRef.current.onCancel && onCancelModal}
          onConfirm={callbacksRef.current.onConfirm && onConfirmModal}
          onClose={callbacksRef.current.onClose && onCloseModal}
          confirmButtonName={valuesForModal?.confirmButtonName}
          cancelButtonName={valuesForModal?.cancelButtonName}
          onCancelAltAction={
            callbacksRef.current.onCancelAltAction && onCancelAltAction
          }
          isConfirmLoading={valuesForModal?.isConfirmLoading || false}
          isCancelLoading={valuesForModal?.isCancelLoading || false}
          altStyling={valuesForModal?.altStyling || false}
          hideCloseButton={valuesForModal?.hideCloseButton || false}
          sx={valuesForModal?.sx || {}}
        >
          {valuesForModal?.children}
        </ConfirmModal>
      )}
    </ModalCTAContext.Provider>
  );
};

export default ModalCTAProvider;
